import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Card, Modal, Form, Input, InputNumber, message, Space, Typography, Spin, Empty, Descriptions, Tag, Popconfirm, Grid } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { fetchPayouts, addPayout, updatePayout, deletePayout } from '../../store/payoutsSlice';
import { fetchBDEs } from '../../store/bdeSlice';
import * as XLSX from 'xlsx';
import { formatCurrency } from '../../utils/formatters';

const { Title, Text } = Typography;

const PayoutHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  // Local state for modal
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingPayout, setEditingPayout] = useState(null);

  // Redux selectors
  const { users, loading: usersLoading } = useSelector((state) => state.bde);
  const { payouts, loading: payoutsLoading } = useSelector((state) => state.payouts);

  // Find current BDE
  const bde = users.find((u) => String(u.id) === String(id));

  // Initial Data Fetch
  useEffect(() => {
    // If id exists, fetch for specific BDE, else fetch all
    dispatch(fetchPayouts(id));

    // Fetch users if not available (handle page refresh or lookup)
    if (users.length === 0) {
      dispatch(fetchBDEs());
    }
  }, [dispatch, id, users.length]);

  // Handle Add/Edit Payout
  const handleSavePayout = async (values) => {
    try {
      setSubmitting(true);

      if (editingPayout) {
        // Update
        await dispatch(updatePayout({
          id: editingPayout._id,
          amount: values.amount,
          transactionId: values.transactionId || '',
          notes: values.notes || '',
        })).unwrap();
        message.success('Payment updated successfully');
      } else {
        // Add
        const payoutData = {
          bdeId: id,
          amount: values.amount,
          transactionId: values.transactionId || '',
          notes: values.notes || '',
        };
        await dispatch(addPayout(payoutData)).unwrap();
        message.success('Payment added successfully');
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingPayout(null);
    } catch (error) {
      message.error(`Failed to ${editingPayout ? 'update' : 'add'} payment: ` + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Delete Payout
  const handleDeletePayout = async (payoutId) => {
    try {
      await dispatch(deletePayout(payoutId)).unwrap();
      message.success('Payment deleted successfully');
    } catch (error) {
      message.error('Failed to delete payment: ' + (error.message || 'Unknown error'));
    }
  };

  // Open Edit Modal
  const openEditModal = (record) => {
    setEditingPayout(record);
    form.setFieldsValue({
      amount: record.amount,
      transactionId: record.transactionId,
      notes: record.notes,
    });
    setIsModalVisible(true);
  };

  // Open Add Modal
  const openAddModal = () => {
    setEditingPayout(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  // Handle Excel Export
  const handleExport = () => {
    if (!payouts || payouts.length === 0) return;

    const data = payouts.map((p) => ({
      'Date': new Date(p.date).toLocaleString(),
      'Amount': p.amount,
      'Transaction ID': p.transactionId || '-',
      'Notes': p.notes || '-',
      'BDE Name': bde?.name || id,
      'BDE ID': id
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payout History");
    XLSX.writeFile(wb, `Payout_History_${bde?.name || id}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Table Columns
  const screens = Grid.useBreakpoint();
  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (text) => new Date(text).toLocaleString(),
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
    // Show BDE Name if looking at all payouts
    ...(!id ? [{
      title: 'BDE Name',
      key: 'bdeName',
      render: (_, record) => {
        const user = users.find(u => String(u.id) === String(record.bdeId));
        return user ? user.name : record.bdeId;
      }
    }] : []),
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => <Text strong type="success">{formatCurrency(amount)}</Text>,
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: 'Transaction ID',
      dataIndex: 'transactionId',
      key: 'transactionId',
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Payout"
            description="Are you sure to delete this payout record?"
            onConfirm={() => handleDeletePayout(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const totalPaid = payouts.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  if (usersLoading && !bde && id) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" tip="Loading BDE details..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>

        {/* Header Section */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <Space align="center">
            {id && (
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/admin/bde-users')}
              >
                Back to List
              </Button>
            )}
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {id ? 'Payout History' : 'All Payouts'}
              </Title>
              {id && (
                <Text type="secondary">
                  {bde ? `${bde.name} (ID: ${bde.id})` : `BDE ID: ${id}`}
                </Text>
              )}
            </div>
          </Space>

          <Space>
            {id && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openAddModal}
              >
                Add Payment
              </Button>
            )}
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={!payouts.length}
            >
              Export Excel
            </Button>
          </Space>
        </div>

        {/* Info Card - Only show if specific BDE */}
        {id && (
          <Card size="small">
            <Descriptions column={{ xxl: 4, xl: 3, lg: 3, md: 3, sm: 2, xs: 1 }}>
              <Descriptions.Item label="BDE Name">{bde?.name || 'Unknown'}</Descriptions.Item>
              <Descriptions.Item label="Mobile">{bde?.mobile || '-'}</Descriptions.Item>
              <Descriptions.Item label="Total Paid">
                <Tag color="green" style={{ fontSize: '14px', padding: '4px 8px' }}>
                  ₹{totalPaid.toFixed(2)}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {/* Data Table */}
          {!screens.md ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {payouts.map(payout => (
                <Card key={payout._id} size="small"
                  actions={[
                    <Button type="link" icon={<EditOutlined />} onClick={() => openEditModal(payout)}>Edit</Button>,
                    <Popconfirm
                      title="Delete Payout"
                      description="Are you sure?"
                      onConfirm={() => handleDeletePayout(payout._id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button type="link" danger icon={<DeleteOutlined />}>Delete</Button>
                    </Popconfirm>
                  ]}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: '#64748b' }}>{new Date(payout.date).toLocaleString()}</span>
                    <Text strong type="success">₹{Number(payout.amount).toFixed(2)}</Text>
                  </div>
                  {!id && (
                    <div style={{ marginBottom: 4 }}>
                      <span style={{ color: '#64748b', marginRight: 8 }}>BDE:</span>
                      <strong>{users.find(u => String(u.id) === String(payout.bdeId))?.name || payout.bdeId}</strong>
                    </div>
                  )}
                  <div style={{ marginBottom: 4 }}>
                    <span style={{ color: '#64748b', marginRight: 8 }}>Trans ID:</span>
                    <span>{payout.transactionId || '-'}</span>
                  </div>
                  <div>
                    <span style={{ color: '#64748b', marginRight: 8 }}>Notes:</span>
                    <span>{payout.notes || '-'}</span>
                  </div>
                </Card>
              ))}
              {payouts.length === 0 && <Empty description="No Payout History" />}
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={payouts}
              rowKey={(record) => record._id || record.id || Math.random()}
              loading={payoutsLoading}
              locale={{
                emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Payout History Found" />
              }}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
            />
          )}
      </Space>

      {/* Add/Edit Payment Modal */}
      <Modal
        title={editingPayout ? "Edit Payment" : "Add New Payment"}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingPayout(null);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSavePayout}
        >
          <Form.Item
            name="amount"
            label="Amount"
            rules={[{ required: true, message: 'Please enter amount' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              prefix="₹"
              placeholder="0.00"
              precision={2}
            />
          </Form.Item>

          <Form.Item
            name="transactionId"
            label="Transaction ID"
            rules={[{ required: false }]}
          >
            <Input placeholder="Enter transaction ID (Optional)" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
            rules={[{ required: false }]}
          >
            <Input.TextArea placeholder="Add any notes..." rows={3} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={submitting}>
              {editingPayout ? "Update Payment" : "Add Payment"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PayoutHistory;
